import {finalize} from "./FloatUtils.js";

/**
 * A type alias for any object with x and y number properties.
 * This allows methods to accept Vec2 instances OR plain objects like `{x: 1, y: 2}`.
 */
export type Vec2Like = {x: number; y: number};

/**
 * A 2D vector class for representing positions, directions, and velocities.
 * Provides methods for common vector operations.
 */
export class Vec2 {
  /**
   * Creates a new 2D vector with the specified coordinates.
   * @param x The x-coordinate
   * @param y The y-coordinate
   */
  constructor(
    public x: number,
    public y: number,
  ) {}

  /**
   * Creates a new vector with coordinates (0, 0).
   * @returns A new zero vector
   */
  static zero(): Vec2 {
    return new Vec2(0, 0);
  }

  /**
   * Creates an array of four vectors representing the corners of a unit square.
   * @returns An array of four Vec2 instances: [(-1,-1), (-1,1), (1,-1), (1,1)]
   */
  static corners(): [Vec2, Vec2, Vec2, Vec2] {
    return [new Vec2(-1, -1), new Vec2(-1, 1), new Vec2(1, -1), new Vec2(1, 1)];
  }

  /**
   * Rounds the vector's coordinates to a consistent precision to avoid floating-point errors.
   * @returns This vector for chaining
   */
  finalize(): this {
    this.x = finalize(this.x);
    this.y = finalize(this.y);
    return this;
  }

  // In-Place Operations
  /**
   * Adds another vector to this vector (in-place).
   * @param other The vector to add
   * @returns This vector for chaining
   */
  add(other: Vec2Like): this {
    this.x = this.x + other.x;
    this.y = this.y + other.y;
    return this;
  }

  /**
   * Subtracts another vector from this vector (in-place).
   * @param other The vector to subtract
   * @returns This vector for chaining
   */
  sub(other: Vec2Like): this {
    this.x = this.x - other.x;
    this.y = this.y - other.y;
    return this;
  }

  /**
   * Multiplies this vector by a scalar (in-place).
   * @param scalar The scalar value to multiply by
   * @returns This vector for chaining
   */
  mul(scalar: number): this {
    this.x = this.x * scalar;
    this.y = this.y * scalar;
    return this;
  }

  /**
   * Multiplies this vector's components by the corresponding components of another vector (in-place).
   * @param other The vector whose components to multiply by
   * @returns This vector for chaining
   */
  mulComponents(other: Vec2Like): this {
    this.x = this.x * other.x;
    this.y = this.y * other.y;
    return this;
  }

  /**
   * Divides this vector by a scalar (in-place).
   * @param scalar The scalar value to divide by
   * @returns This vector for chaining
   */
  div(scalar: number): this {
    this.x = this.x / scalar;
    this.y = this.y / scalar;
    return this;
  }

  /**
   * Normalizes this vector to have a length of 1 (in-place).
   * @throws Error if the vector has zero length
   * @returns This vector for chaining
   */
  normalize(): this {
    const mag = this.length();
    if (mag === 0) {
      throw new Error("Cannot normalize a zero vector");
    }
    this.div(mag);
    return this;
  }

  // Methods that return a scalar
  /**
   * Calculates the dot product of this vector with another vector.
   * @param other The vector to calculate the dot product with
   * @returns The dot product value
   */
  dot(other: Vec2Like): number {
    return this.x * other.x + this.y * other.y;
  }

  /**
   * Calculates the 2D cross product of this vector with another vector.
   * @param other The vector to calculate the cross product with
   * @returns The cross product value (scalar in 2D)
   */
  cross(other: Vec2Like): number {
    return this.x * other.y - this.y * other.x;
  }

  /**
   * Calculates the length (magnitude) of this vector.
   * @returns The length of the vector
   */
  length(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  /**
   * Calculates the distance from this vector to another vector.
   * @param other The vector to calculate the distance to
   * @returns The distance between the two vectors
   */
  distanceTo(other: Vec2Like): number {
    // We can pass `other` directly to `sub` because it now accepts Vec2Like
    return this.clone().sub(other).length();
  }

  /**
   * Performs linear interpolation between this vector and another vector (in-place).
   * @param other The target vector to interpolate towards
   * @param t The interpolation parameter (0-1), where 0 is this vector and 1 is the other vector
   * @returns This vector for chaining
   */
  lerp(other: Vec2Like, t: number): this {
    // Create a new Vec2 from `other` to safely perform chained operations,
    // as a plain Vec2Like object won't have methods like .sub() or .mul().
    const tempVec = new Vec2(other.x, other.y);
    return this.add(tempVec.sub(this).mul(t));
  }

  // Utility Methods
  /**
   * Creates a new vector with the same coordinates as this vector.
   * @returns A new Vec2 instance with the same x and y values
   */
  clone(): Vec2 {
    return new Vec2(this.x, this.y);
  }

  /**
   * Returns a string representation of this vector.
   * @returns A string in the format "Vec2(x, y)"
   */
  toString(): string {
    return `Vec2(${this.x}, ${this.y})`;
  }

  /**
   * Checks if this vector is equal to another vector.
   * @param other The vector to compare with
   * @returns True if both x and y coordinates are equal, false otherwise
   */
  equals(other: Vec2Like): boolean {
    return this.x === other.x && this.y === other.y;
  }

  /**
   * Sets the coordinates of this vector to the specified values.
   * @param x The new x-coordinate
   * @param y The new y-coordinate
   * @returns This vector for chaining
   */
  set(x: number, y: number) {
    this.x = x;
    this.y = y;
    return this;
  }

  /**
   * Applies Math.floor to both coordinates of this vector (in-place).
   * @returns This vector for chaining
   */
  floor(): this {
    this.x = Math.floor(this.x);
    this.y = Math.floor(this.y);
    return this;
  }

  /**
   * Copies the coordinates from another vector to this vector (in-place).
   * @param other The vector to copy coordinates from
   * @returns This vector for chaining
   */
  copyFrom(other: Vec2Like): this {
    this.x = other.x;
    this.y = other.y;
    return this;
  }

  /**
   * Rotates this vector by the specified angle (in-place).
   * @param angle The angle in radians to rotate by
   * @returns This vector for chaining
   */
  rotate(angle: number): this {
    const cos = finalize(Math.cos(angle));
    const sin = finalize(Math.sin(angle));
    const x = this.x * cos - this.y * sin;
    const y = this.x * sin + this.y * cos;
    this.x = x;
    this.y = y;
    return this;
  }

  sqrt(): this {
    this.x = Math.sign(this.x) * Math.sqrt(Math.abs(this.x));
    this.y = Math.sign(this.y) * Math.sqrt(Math.abs(this.y));
    return this;
  }
}
